import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPencil,
  faTrash,
  faMagnifyingGlass,
  faStar,
  faCalculator,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';

const ScenarioManagement = () => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    is_active: '',
    is_default: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rulesJson: {
      material_cost_change: 0,
      labor_cost_change: 0,
      area_affected_change: 0,
      repair_procedure: 'touch-up',
      confined_space_multiplier: 1.0,
      scaffolding_multiplier: 1.0
    },
    degradationRules: {
      C2: { asymptote: 0.8, decay_rate: 0.15 },
      C3: { asymptote: 0.9, decay_rate: 0.20 },
      C5: { asymptote: 0.95, decay_rate: 0.25 }
    },
    triggerCriteria: {
      repair_threshold: 0.3,
      maintenance_threshold: 0.1,
      monte_carlo_ranges: {
        cost_variation: 0.1,
        decay_variation: 0.05
      }
    },
    overrides: {
      material_costs: {},
      labor_rates: {},
      surface_prep_costs: {},
      repair_rules: {}
    },
    timeHorizonYears: 30,
    discountRate: 0.04,
    damageSplitA: 55.0,
    damageSplitB: 35.0,
    damageSplitC: 10.0,
    costYear: 2024,
    isDefault: false,
    isActive: true
  });

  useEffect(() => {
    fetchScenarios();
  }, [pagination.currentPage, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchScenarios = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: pagination.currentPage.toString(),
        limit: '20'
      });

      if (filters.search) params.append('search', filters.search);
      if (filters.is_active !== '') params.append('is_active', filters.is_active);
      if (filters.is_default !== '') params.append('is_default', filters.is_default);

      const response = await api.get(`/scenarios?${params}`);

      if (response.data.success) {
        setScenarios(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch scenarios');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      rulesJson: {
        material_cost_change: 0,
        labor_cost_change: 0,
        area_affected_change: 0,
        repair_procedure: 'touch-up',
        confined_space_multiplier: 1.0,
        scaffolding_multiplier: 1.0
      },
      degradationRules: {
        C2: { asymptote: 0.8, decay_rate: 0.15 },
        C3: { asymptote: 0.9, decay_rate: 0.20 },
        C5: { asymptote: 0.95, decay_rate: 0.25 }
      },
      triggerCriteria: {
        repair_threshold: 0.3,
        maintenance_threshold: 0.1,
        monte_carlo_ranges: {
          cost_variation: 0.1,
          decay_variation: 0.05
        }
      },
      overrides: {
        material_costs: {},
        labor_rates: {},
        surface_prep_costs: {},
        repair_rules: {}
      },
      timeHorizonYears: 30,
      discountRate: 0.04,
      damageSplitA: 55.0,
      damageSplitB: 35.0,
      damageSplitC: 10.0,
      costYear: 2024,
      isDefault: false,
      isActive: true
    });
    setEditingScenario(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate damage split totals 100%
      const totalSplit = parseFloat(formData.damageSplitA) + parseFloat(formData.damageSplitB) + parseFloat(formData.damageSplitC);
      if (Math.abs(totalSplit - 100.0) > 0.01) {
        setError('Damage split percentages must total 100%');
        setLoading(false);
        return;
      }

      const submitData = {
        ...formData,
        degradation_rules: formData.degradationRules,
        trigger_criteria: formData.triggerCriteria,
        overrides: formData.overrides
      };

      if (editingScenario) {
        await api.put(`/scenarios/${editingScenario.id}`, submitData);
      } else {
        await api.post('/scenarios', submitData);
      }
      
      setShowModal(false);
      resetForm();
      fetchScenarios();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save scenario');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (scenario) => {
    setEditingScenario(scenario);
    setFormData({
      name: scenario.name,
      description: scenario.description || '',
      rulesJson: (() => {
        const rules = typeof scenario.rulesJson === 'string' ? JSON.parse(scenario.rulesJson) : scenario.rulesJson;
        return rules || {
          material_cost_change: 0,
          labor_cost_change: 0,
          area_affected_change: 0,
          repair_procedure: 'touch-up',
          confined_space_multiplier: 1.0,
          scaffolding_multiplier: 1.0
        };
      })(),
      degradationRules: (() => {
        const degradation = typeof scenario.degradationRules === 'string' ? JSON.parse(scenario.degradationRules) : scenario.degradationRules;
        return degradation || {
          C2: { asymptote: 0.8, decay_rate: 0.15 },
          C3: { asymptote: 0.9, decay_rate: 0.20 },
          C5: { asymptote: 0.95, decay_rate: 0.25 }
        };
      })(),
      triggerCriteria: (() => {
        const trigger = typeof scenario.triggerCriteria === 'string' ? JSON.parse(scenario.triggerCriteria) : scenario.triggerCriteria;
        return trigger || {
          repair_threshold: 0.3,
          maintenance_threshold: 0.1,
          monte_carlo_ranges: {
            cost_variation: 0.1,
            decay_variation: 0.05
          }
        };
      })(),
      overrides: (() => {
        const overrides = typeof scenario.overrides === 'string' ? JSON.parse(scenario.overrides) : scenario.overrides;
        return overrides || {
          material_costs: {},
          labor_rates: {},
          surface_prep_costs: {},
          repair_rules: {}
        };
      })(),
      timeHorizonYears: scenario.timeHorizonYears,
      discountRate: scenario.discountRate,
      damageSplitA: scenario.damageSplitA,
      damageSplitB: scenario.damageSplitB,
      damageSplitC: scenario.damageSplitC,
      costYear: scenario.costYear,
      isDefault: scenario.isDefault,
      isActive: scenario.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (scenario) => {
    if (window.confirm(`Delete scenario "${scenario.name}"?`)) {
      try {
        await api.delete(`/scenarios/${scenario.id}`);
        fetchScenarios();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete');
      }
    }
  };


  const formatRuleValue = (value) => {
    if (typeof value === 'number') {
      return value > 0 ? `+${(value * 100).toFixed(1)}%` : `${(value * 100).toFixed(1)}%`;
    }
    return value;
  };

  const getRuleColor = (value) => {
    if (typeof value === 'number') {
      return value > 0 ? 'text-green-600' : value < 0 ? 'text-red-600' : 'text-gray-600';
    }
    return 'text-blue-600';
  };

  // Calculation Preview Functions
  const calculateDegradation = (asymptote, decayRate, years) => {
    return asymptote * (1 - Math.exp(-decayRate * years));
  };

  const calculateNPV = (cost, year, discountRate) => {
    return cost / Math.pow(1 + discountRate, year);
  };

  const generatePreview = () => {
    const sampleData = {
      surfaceArea: 1000, // m²
      confinedMultiplier: formData.rulesJson.confined_space_multiplier,
      yearsSincePainted: 5,
      corrosionCategory: 'C3'
    };

    const degradation = calculateDegradation(
      formData.degradationRules[sampleData.corrosionCategory].asymptote,
      formData.degradationRules[sampleData.corrosionCategory].decay_rate,
      sampleData.yearsSincePainted
    );

    const degradedArea = sampleData.surfaceArea * sampleData.confinedMultiplier * degradation;
    
    const baseCost = 25.77; // CAD/m² from blended surface prep
    const modifiedCost = baseCost * (1 + formData.rulesJson.material_cost_change);
    const totalCost = modifiedCost * degradedArea;
    
    const npv = calculateNPV(totalCost, sampleData.yearsSincePainted, formData.discountRate);

    return {
      degradation: (degradation * 100).toFixed(1),
      degradedArea: degradedArea.toFixed(0),
      baseCost: baseCost.toFixed(2),
      modifiedCost: modifiedCost.toFixed(2),
      totalCost: totalCost.toFixed(2),
      npv: npv.toFixed(2)
    };
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Scenario Management</h1>
        <p className="text-gray-600">
          Create and manage simulation scenarios with rule-based cost modifications, degradation parameters, 
          and master data overrides. Each scenario defines complete simulation parameters for Monte Carlo analysis.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search scenarios..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <FontAwesomeIcon 
                icon={faMagnifyingGlass} 
                className="absolute left-3 top-3 text-gray-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              name="is_active"
              value={filters.is_active}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default</label>
            <select
              name="is_default"
              value={filters.is_default}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="true">Default</option>
              <option value="false">Non-Default</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setFilters({ search: '', is_active: '', is_default: '' });
                setPagination(prev => ({ ...prev, currentPage: 1 }));
              }}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Add Button */}
      <div className="mb-4">
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add Scenario
        </button>
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rules
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Degradation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Horizon
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {scenarios.map((scenario) => (
                  <tr key={scenario.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {scenario.isDefault === 1 && (
                          <FontAwesomeIcon icon={faStar} className="text-yellow-500 mr-2" />
                        )}
                        <div className="text-sm font-medium text-gray-900">
                          {scenario.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {scenario.rulesJson && (() => {
                          const rules = typeof scenario.rulesJson === 'string' ? JSON.parse(scenario.rulesJson) : scenario.rulesJson;
                          return Object.entries(rules).slice(0, 3).map(([key, value]) => (
                            <div key={key} className="flex items-center mb-1">
                              <span className="text-xs text-gray-500 w-16 truncate">{key}:</span>
                              <span className={`text-xs font-medium ${getRuleColor(value)}`}>
                                {formatRuleValue(value)}
                              </span>
                            </div>
                          ));
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {scenario.degradationRules && (() => {
                          const degradation = typeof scenario.degradationRules === 'string' ? JSON.parse(scenario.degradationRules) : scenario.degradationRules;
                          return Object.entries(degradation).map(([cat, params]) => (
                            <div key={cat} className="text-xs">
                              {cat}: a={params.asymptote}, b={params.decay_rate}
                            </div>
                          ));
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {scenario.timeHorizonYears} years
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        scenario.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {scenario.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(scenario)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faPencil} />
                      </button>
                      <button
                        onClick={() => handleDelete(scenario)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {pagination.currentPage} of {pagination.totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
              disabled={!pagination.hasPrevPage}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
              disabled={!pagination.hasNextPage}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Modal - Will continue in next part */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-5/6 lg:w-4/5 xl:w-3/4 shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingScenario ? 'Edit Scenario' : 'Add New Scenario'}
                </h3>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center"
                >
                  <FontAwesomeIcon icon={faCalculator} className="mr-1" />
                  {showPreview ? 'Hide' : 'Show'} Preview
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              {/* Calculation Preview */}
              {showPreview && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <h4 className="text-md font-medium text-blue-900 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faEye} className="mr-2" />
                    Calculation Preview (Sample: 1000m², C3, 5 years)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700 font-medium">Degradation:</span>
                      <span className="ml-2 text-blue-900">{generatePreview().degradation}%</span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">Degraded Area:</span>
                      <span className="ml-2 text-blue-900">{generatePreview().degradedArea} m²</span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">Base Cost:</span>
                      <span className="ml-2 text-blue-900">${generatePreview().baseCost}/m²</span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">Modified Cost:</span>
                      <span className="ml-2 text-blue-900">${generatePreview().modifiedCost}/m²</span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">Total Cost:</span>
                      <span className="ml-2 text-blue-900">${generatePreview().totalCost}</span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">NPV:</span>
                      <span className="ml-2 text-blue-900">${generatePreview().npv}</span>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-blue-700">
                    <strong>Formula:</strong> D(t) = a × (1 − e^(−b × t)) | NPV = Cost ÷ (1 + r)^t
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Scenario Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Base Case, Optimistic, Pessimistic"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cost Year *
                    </label>
                    <input
                      type="number"
                      value={formData.costYear}
                      onChange={(e) => setFormData({ ...formData, costYear: parseInt(e.target.value) })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe the scenario assumptions and conditions..."
                  />
                </div>

                {/* Continue with more sections in next part... */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : editingScenario ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenarioManagement;